import { USER_ROLE_LEVEL, UserRole, type IUser } from '@mirasoles/shared';

/**
 * La herencia es total: un usuario de nivel N puede todo lo que puede nivel N+1.
 * La comparacion es por NIVEL, no por el valor crudo del rol — varios roles
 * distintos (ej. Administracion, Compras y Logistica, Supervision) comparten
 * nivel 2. Espejo exacto de la regla de `PermissionsGuard` en el backend —
 * mantenerlas en sync a mano, ya que ninguna corre en el otro lado.
 *
 * Chequeo explicito de rol no reconocido (no solo confiar en que `<=` con
 * `undefined` da `false`): es solo ocultamiento de UI, el backend es el
 * limite real, pero un chequeo implicito que depende de una sutileza de JS
 * es fragil ante el proximo cambio de operador — ver el mismo fix en
 * `PermissionsGuard` del backend, que si era explotable de verdad.
 */
export function hasRole(user: IUser | null | undefined, role: UserRole): boolean {
  if (!user) return false;
  const userLevel = USER_ROLE_LEVEL[user.role];
  if (userLevel === undefined) return false;
  return userLevel <= USER_ROLE_LEVEL[role];
}

/**
 * #221: aprobar una OC pendiente es exclusivo de Gerencia y Administración (por rol exacto, no por nivel: Compras y
 * Logística y Supervisión comparten nivel con Administración). Espeja `@RequiresExactRoles(UserRole.ADMINISTRACION,
 * UserRole.GERENCIA)` del backend.
 */
export function canApprovePurchaseOrders(user: IUser | null | undefined): boolean {
  return user?.role === UserRole.GERENCIA || user?.role === UserRole.ADMINISTRACION;
}

/**
 * Roles EXACTOS que administran la tabla maestra del checklist BPM (Administración y Gerencia) — a
 * diferencia de `hasRole`, no hereda por nivel (Compras, Supervisión y el resto quedan afuera). Espeja
 * `@RequiresExactRoles(UserRole.ADMINISTRACION, UserRole.GERENCIA)` del backend.
 */
export function canManageBpmChecklist(user: IUser | null | undefined): boolean {
  return user?.role === UserRole.ADMINISTRACION || user?.role === UserRole.GERENCIA;
}

export function isCocina(user: IUser | null | undefined): boolean {
  return user?.role === UserRole.COCINA;
}

/**
 * ISSUE-143: `CLIENTE_RRHH` es un rol externo, fuera de la jerarquía interna
 * (`hasRole` no tiene sentido para él — su nivel es mayor a cualquier rol
 * interno a propósito, ver `design.md`). Se chequea aparte, por igualdad
 * exacta de rol, para decidir qué shell de rutas renderizar.
 */
export function isClientPortalUser(user: IUser | null | undefined): boolean {
  return user?.role === UserRole.CLIENTE_RRHH;
}

/** Rol externo Proveedor: entra sólo a `/proveedor/*` (portal de proveedores). */
export function isSupplierPortalUser(user: IUser | null | undefined): boolean {
  return user?.role === UserRole.PROVEEDOR;
}
